import { useState } from 'react';
import { useI18n } from '@react/i18n';
import { VratkyApi } from '../api/vratkyApi';
import { isLookupError, LookupData, LookupErrorCode, Page, SuggestedEmails, UserInput } from '../types';
import { UpdateUserInput } from '../VratkyApp';

interface Props {
    api: VratkyApi;
    userInput: UserInput;
    update: UpdateUserInput;
    data: LookupData | null;
    emailHints: boolean;
    setData: (data: LookupData) => void;
    setContact: (contact: Pick<UserInput, 'contact_name' | 'contact_phone' | 'contact_email'>) => void;
    link: (page: Page) => void;
}

export function BaseInput({ api, userInput, update, data, emailHints, setData, setContact, link }: Props) {
    const t = useI18n();
    const [error, setError] = useState<LookupErrorCode | 'backend_unavailable' | null>(null);
    const [busy, setBusy] = useState(false);
    const [suggested, setSuggested] = useState<SuggestedEmails | null>(null);

    /** Nápověda e-mailů k dokladu (port getEmail.php) – selhání je tichý stav bez nápovědy. */
    const loadSuggestedEmails = async () => {
        const cislo = userInput.user_cislo_dokladu.trim();
        if (!emailHints || cislo === '') {
            return;
        }
        try {
            setSuggested(await api.suggestEmails(cislo));
        } catch {
            setSuggested(null);
        }
    };

    // pořadí = priorita nápovědy, stejný e-mail se nenabízí dvakrát
    const hints = ([
        [suggested?.obj_email, t('email z objednávky')],
        [suggested?.par_email, t('email z IS')],
        [suggested?.par_email_login, t('email ze shopu')],
    ] as [string | undefined, string][])
        .filter(([email]) => !!email)
        .filter(([email], index, all) => all.findIndex(([other]) => other === email) === index);

    const checkData = async () => {
        setBusy(true);
        try {
            const result = await api.lookup(userInput.user_cislo_dokladu, userInput.user_email, userInput.no_order_email);
            if (isLookupError(result)) {
                setError(result.error_message);
                return;
            }
            if (result.faktury.length > 1) {
                setError('multiple_invoices');
                setData(result);
                return;
            }

            setError(null);
            setData(result);
            setContact({
                contact_phone: result.objednavka.obj_telefon || result.objednavka.par_tel || result.objednavka.par_mobil || '',
                contact_name: result.objednavka.nazev_odb?.split('#')[0]?.trim() || result.objednavka.nazev_odb || '',
                contact_email: userInput.user_email,
            });
            link('product_selector');
        } catch {
            setError('backend_unavailable');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="vr-step">
            <div className="vr-field">
                <label>{t('Číslo objednávky nebo faktury')}<span className="vr-required">*</span></label>
                <input
                    type="text"
                    className="form-control"
                    value={userInput.user_cislo_dokladu}
                    onChange={(e) => {
                        setSuggested(null);
                        update('user_cislo_dokladu', e.target.value);
                    }}
                    onBlur={() => void loadSuggestedEmails()}
                    placeholder="..."
                />
            </div>

            <div className="vr-field">
                <label>{t('Email, pod kterým jste objednávali')}<span className="vr-required">*</span></label>
                <input
                    type="text"
                    className="form-control"
                    disabled={userInput.no_order_email}
                    value={userInput.user_email}
                    onChange={(e) => update('user_email', e.target.value.trim())}
                    placeholder={t('jmeno@domena.cz')}
                />
                {!userInput.no_order_email && hints.length > 0 && (
                    <ul className="vr-hints">
                        {hints.map(([email, source]) => (
                            <li key={email}>
                                <a
                                    href="#email"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        update('user_email', email as string);
                                    }}
                                >
                                    {email}
                                </a>
                                {' \u2013 '}
                                {source}
                            </li>
                        ))}
                    </ul>
                )}
                <label className="vr-check">
                    <input
                        type="checkbox"
                        checked={userInput.no_order_email}
                        onChange={(e) => update('no_order_email', e.target.checked)}
                    />
                    {t('Bez emailu (koupeno osobně na prodejně)')}
                </label>
            </div>

            {error && (
                <div className="vr-alert vr-alert--danger" role="alert">
                    {error === 'wrong_email' && t('Nesprávný email')}
                    {error === 'order_not_found' && t('Objednávka nenalezena')}
                    {error === 'order_not_invoiced' && t('K této objednávce zatím nebyla vystavena faktura, vratku proto zatím nelze založit. Zkuste to prosím později nebo nás kontaktujte.')}
                    {error === 'no_order_number' && t('Není zadáno číslo objednávky')}
                    {error === 'no_email' && t('Není zadán email')}
                    {error === 'wrong_doc_number' && t('Nesprávně zadané číslo dokladu')}
                    {error === 'ordered_with_email' && t('Objednávka byla s emailem, prosíme zadejte jej')}
                    {error === 'backend_unavailable' && t('Službu se nepodařilo kontaktovat. Zkuste to prosím později.')}
                    {error === 'multiple_invoices' && (
                        <>
                            {t('Objednávka byla rozdělena na více faktur. Prosím zadejte či vyberte jednu z nich:')}
                            <br />
                            {data?.faktury.map((item) => (
                                <span key={item.cislo_faktury}>
                                    <a
                                        href="#f"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            update('user_cislo_dokladu', item.cislo_faktury);
                                        }}
                                    >
                                        {item.cislo_faktury}
                                    </a>
                                    <br />
                                </span>
                            ))}
                        </>
                    )}
                </div>
            )}

            <div className="vr-actions">
                <button type="button" className="vr-btn vr-btn--secondary" onClick={() => link('init')}>{t('Zpět')}</button>
                <button type="button" className="vr-btn vr-btn--primary" disabled={busy} onClick={() => void checkData()}>
                    {t('Pokračovat')}
                </button>
            </div>
        </div>
    );
}
